// The share sheet: sharing a result sends your challenge on its edition, through a share link
// (/s/<code>) that opens it and unfurls in a chat with a picture drawn for it (your rank, score and
// grid). The sheet shows what lands in their chat first, then Send and Copy link, and inviting
// rivals in the app.
import { useState } from 'react'
import type { Challenge, EditionInfo, Me, Play, ShareRequest, ShareVia } from '@wbwl/contract'
import { rankLine } from '@wbwl/rules'
import { api } from '../lib/api.ts'
import { fmt, SITE, storage } from '../lib/game.ts'
import { challengeLine, copyText, nativeShare } from '../lib/share.ts'
import { useRivalInvites } from './challenge-ui.tsx'
import { Icon } from './Icon.tsx'
import { Avatar, Sheet, useToast } from './shell.tsx'

const RIDDLE_KEY = 'lof:riddle'

/** The message, a line at a time: your rank and score, then the ash riddle if it's on and you raised a phoenix. */
export function shareLines(info: EditionInfo, play: Play, riddle: boolean): string[] {
  const lines = [challengeLine(info.number, play.standing.name, play.scores.total)]
  const phx = play.scores.phoenix
  if (riddle && phx) lines.push(`🔥 My ashes: ${[...play.ashes].sort().join(' ')}. I raised a ${phx.total}-point phoenix from them. Can you find it?`)
  return lines
}

/**
 * `challenge` is null until Results has made (or found) yours; the link and its picture wait for it.
 * `code` is the share link's, which Results has registered with the challenge.
 */
export function ShareSheet({ open, onClose, info, play, me, challenge: ch, onChange, code }: {
  open: boolean
  onClose(): void
  info: EditionInfo
  play: Play
  me: Me
  challenge: Challenge | null
  onChange(ch: Challenge): void
  code: string
}) {
  const toast = useToast()
  const { pickable, sent, sending, invite } = useRivalInvites(open ? ch : null, onChange)
  const [riddleOn, setRiddleOn] = useState(() => storage.get(RIDDLE_KEY) !== 'off')
  const [loaded, setLoaded] = useState<string | null>(null)
  const phx = play.scores.phoenix
  const riddle = riddleOn && !!phx
  const lines = shareLines(info, play, riddle)
  const link = `${SITE}/s/${code}`
  // The same picture the link unfurls with, from this site so it shows in development too.
  const image = ch ? `/og/c/${ch.code}.png?v=${play.scores.total}` : null

  // Each send names the challenge again, so the link opens it even if registering it failed.
  function shared(via: ShareVia, withRiddle = false) {
    if (!ch) return
    api.post('/shares', { code, edition: info.number, via, challenge: ch.code, ...(via === 'link' ? { riddle: withRiddle } : {}) } satisfies ShareRequest).catch(() => {})
  }
  async function send() {
    const message = [...lines, link].join('\n')
    const how = await nativeShare(message)
    if (how === 'shared') shared('link', riddle)
    if (how !== 'unavailable') return
    const copied = await copyText(message)
    toast(copied ? <><Icon name="check" />Message copied — paste it anywhere</> : 'Couldn’t copy. Try Copy link.')
    if (copied) shared('link', riddle)
  }
  async function copyLink() {
    const copied = await copyText(link)
    toast(copied ? <><Icon name="check" />Link copied — paste it anywhere</> : 'Couldn’t copy the link. Try again.')
    if (copied) shared('link')
  }
  function toggleRiddle() {
    setRiddleOn(!riddleOn)
    storage.set(RIDDLE_KEY, riddleOn ? 'off' : 'on')
  }

  return (
    <Sheet open={open} onClose={onClose} label={`Share No. ${info.number}`}>
      <div className="share">
        <div className="share-head">
          <h2>Share No. {info.number}</h2>
          <button type="button" className="ibtn boxed" aria-label="Close" onClick={onClose}><Icon name="close" /></button>
        </div>
        <p className="muted share-lede">Friends get a link to {info.isToday ? 'today’s board' : `No. ${info.number}`} and play to beat your {fmt(play.scores.total)}. They see your rank and score, not your words.</p>

        <div className="share-see">
          <div className="eyebrow">What lands in their chat</div>
          <div className="share-chat">
            <div className="bubble">{lines.map((line) => <p key={line}>{line}</p>)}</div>
            <div className="link-card">
              <div className="lc-img">
                {image && <img src={image} alt={`${me.displayName}’s board, words hidden, against an empty one`} className={loaded === image ? 'on' : ''} onLoad={() => setLoaded(image)} />}
              </div>
              <div className="lc-cap"><b>{rankLine(me.displayName, play.standing.name)} · Letters on Fire No. {info.number}</b><span>{new URL(SITE).host}</span></div>
            </div>
          </div>
        </div>

        {phx && (
          <label className="share-opt">
            <span><b>Add an ash riddle</b><span className="muted">Your {play.ashes.length} ashes and what your phoenix scored, so friends can guess the word.</span></span>
            <input type="checkbox" role="switch" className="switch" checked={riddleOn} onChange={toggleRiddle} />
          </label>
        )}

        <div className="share-send">
          <button type="button" className="btn ink" onClick={send} disabled={!ch}><Icon name="share" />Send</button>
          <button type="button" className="btn ghost" onClick={copyLink} disabled={!ch}><Icon name="link" />Copy link</button>
        </div>

        {ch && pickable.length > 0 && (
          <div className="share-inapp">
            <span className="eyebrow">Or in the app</span>
            {pickable.map(({ player: p }) => (sent(p.id)
              ? <span className="pal sent" key={p.id}><Avatar id={p.id} name={p.displayName} src={p.avatar} size={30} /><span className="nm">{p.displayName}</span><Icon name="check" size={15} /></span>
              : (
                <button type="button" className="pal" key={p.id} disabled={sending === p.id || !ch.open} onClick={() => invite(p.id)} aria-label={`Send it to ${p.displayName} in the app`}>
                  <Avatar id={p.id} name={p.displayName} src={p.avatar} size={30} /><span className="nm">{p.displayName}</span><Icon name="plus" size={15} />
                </button>
              )))}
          </div>
        )}
      </div>
    </Sheet>
  )
}
